import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { CostDetails as CostDetailsModel, MaterialCosts, PayCosts, ShiftType } from "@/graphql/__generated__/graphql";
import { formatNumberLocale } from "@/utils/helpers";

interface CostItemRow {
  label: string;
  value: number;
}

interface CostItemComponentProps {
  title: string;
  totalCosts: number;
  costs: CostItemRow[];
}

/**
 * CostItemComponent - Renders a single cost group (e.g. pay costs or material costs) with its total and rows.
 *
 * @component
 * @example
 * <CostItemComponent
 *   title="Pay Costs"
 *   totalCosts={1234.56}
 *   costs={[{ label: "Base Wage", value: 1000 }]}
 * />
 */
function CostItemComponent({ title, totalCosts, costs }: CostItemComponentProps) {
  const intl = useIntl();

  return (
    <div className="flex-col-4 grow min-w-[260px]">
      <div>
        <div className="flex justify-between py-2">
          <h3 className="font-medium">{title}</h3>
          <div className="font-semibold">{formatNumberLocale(intl, totalCosts, "currency")}</div>
        </div>
        <Separator />
      </div>

      <div data-cy="sidebar-cost-item-component-content-container" className="flex flex-col gap-2 pt-2 font-normal">
        {costs.map((cost) => (
          <div className="horizontal-between-subtle-label" key={`sidebar-cost-content-item-${cost.label}`}>
            <span>{cost.label}</span>
            <span>{formatNumberLocale(intl, cost.value, "currency")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const PayCostsComponent = ({ data }: { data: PayCosts }) => {
  const { t } = useTranslation(["components", "common"]);

  return (
    <CostItemComponent
      title={t("costsummary.paycosts.title")}
      totalCosts={data.totalPayCosts}
      costs={[
        { label: t("costsummary.paycosts.labels.basewage"), value: data.baseWage },
        { label: t("costsummary.paycosts.labels.nontariffallowance"), value: data.nonTariffAllowances },
        { label: t("costsummary.paycosts.labels.wage"), value: data.wage },
        { label: t("costsummary.paycosts.labels.bonuspay"), value: data.bonusPay },
        { label: t("costsummary.paycosts.labels.training"), value: data.training },
        { label: t("costsummary.paycosts.labels.briefing"), value: data.briefing },
        { label: t("costsummary.paycosts.labels.fluctuationbriefing"), value: data.fluctuationBriefing },
        { label: t("costsummary.paycosts.labels.sickness"), value: data.sickness },
        { label: t("costsummary.paycosts.labels.vacation"), value: data.vacation },
        { label: t("costsummary.paycosts.labels.vacationbonus"), value: data.vacationBonus },
        { label: t("costsummary.paycosts.labels.christmasbonus"), value: data.christmasBonus },
        { label: t("costsummary.paycosts.labels.holidaypay"), value: data.holidayPay },
        { label: t("costsummary.paycosts.labels.socialsecurity"), value: data.socialSecurity },
      ]}
    />
  );
};

const MaterialCostsComponent = ({ data }: { data: MaterialCosts }) => {
  const { t } = useTranslation(["components", "common"]);

  return (
    <CostItemComponent
      title={t("costsummary.materialcosts.title")}
      totalCosts={data.totalMaterialCosts}
      costs={[
        { label: t("costsummary.materialcosts.labels.uniform"), value: data.uniform },
        { label: t("costsummary.materialcosts.labels.trainingfees"), value: data.trainingFees },
        { label: t("costsummary.materialcosts.labels.othermaterial"), value: data.otherMaterial },
        { label: t("costsummary.materialcosts.labels.professionalassociation"), value: data.professionalAssociation },
        { label: t("costsummary.materialcosts.labels.liabilityinsurance"), value: data.liabilityInsurance },
      ]}
    />
  );
};

const costDetailsTypeOrder = [
  ShiftType.Cumulated,
  ShiftType.Day,
  ShiftType.Night,
  ShiftType.Sunday,
  ShiftType.SundayNight,
  ShiftType.Holiday,
  ShiftType.HolidayNight,
];

interface CostDetailsProps {
  /** Cost details for the currently selected service */
  costDetails: CostDetailsModel[];
}

/**
 * CostDetailsSection - Sidebar section that shows cost details grouped by shift type.
 * Uses an accordion per type, and within each type shows pay and material cost breakdowns.
 *
 * @component
 * @example
 * <CostDetailsSection costDetails={service.costDetails} />
 */
function CostDetailsSection({ costDetails }: CostDetailsProps) {
  const { t } = useTranslation(["components", "common"]);
  const intl = useIntl();

  const costDetailsSorted = useMemo(() => {
    return [...(costDetails ?? [])].sort(
      (a, b) => costDetailsTypeOrder.indexOf(a.type) - costDetailsTypeOrder.indexOf(b.type)
    );
  }, [costDetails]);

  const hasCostDetails = costDetailsSorted.length > 0;

  return (
    <Card className="shadow-none p-4">
      <CardHeader className="p-0 pb-4">
        <CardTitle className="text-sm font-bold">{t("costsummary.title")}</CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {!hasCostDetails ? (
          <div className="text-sm text-slate-500 text-center">{t("common:nodataavailable")}</div>
        ) : (
          <Accordion type="single" collapsible className="w-full space-y-2">
            {costDetailsSorted.map((costDetail) => (
              <AccordionItem
                key={`sidebar-cost-details-accordion-${costDetail.type}`}
                value={costDetail.type}
                className="border last:border-b-2 rounded-md px-3"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div data-cy="sidebar-cost-summary-type-accordion-trigger" className="flex grow justify-between">
                    <h2 className="text-sm font-medium">{t(`common:costdetailstype.${costDetail.type.toLowerCase()}`)}</h2>
                    <div className="flex gap-4 mr-4">
                      <span className="text-gray-400 font-normal">{t("common:total")}</span>
                      <span data-cy="sidebar-cost-summary-accordion-total">
                        {formatNumberLocale(intl, costDetail.totalCosts, "currency")}
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent
                  data-cy="sidebar-cost-summary-type-accordion-content"
                  className="flex flex-wrap gap-8 justify-between"
                >
                  <PayCostsComponent data={costDetail.payCosts} />
                  <MaterialCostsComponent data={costDetail.materialCosts} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

export default CostDetailsSection;
